import React, { useState, useEffect } from 'react';
import { Play, Loader2, Copy, Check, AlertCircle } from 'lucide-react';

const ApiExplorer = ({ method, path, description, parameters = {}, requestBody = null }) => {
  const [apiKey, setApiKey] = useState('');
  const [pathParams, setPathParams] = useState({});
  const [queryParams, setQueryParams] = useState({});
  const [bodyParams, setBodyParams] = useState({});
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  // Use environment variable if available, otherwise default to localhost:8000
  // This works when running locally or when frontend accesses API via host port mapping
  const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

  // Load API key from localStorage
  useEffect(() => {
    const savedApiKey = localStorage.getItem('api_key');
    if (savedApiKey) {
      setApiKey(savedApiKey);
    }
  }, []);

  // Save API key to localStorage
  const handleApiKeyChange = (value) => {
    setApiKey(value);
    if (value) {
      localStorage.setItem('api_key', value);
    } else {
      localStorage.removeItem('api_key');
    }
  };

  // Extract path parameters from path
  useEffect(() => {
    const pathParamMatches = path.match(/\{(\w+)\}/g);
    if (pathParamMatches) {
      const newPathParams = {};
      pathParamMatches.forEach(match => {
        const paramName = match.slice(1, -1);
        newPathParams[paramName] = pathParams[paramName] || '';
      });
      setPathParams(newPathParams);
    }
  }, [path]);

  // Initialize body params from requestBody prop
  useEffect(() => {
    if (requestBody && typeof requestBody === 'object') {
      setBodyParams(requestBody);
    }
  }, [requestBody]);

  const buildUrl = () => {
    let url = `${BASE_URL}${path}`;

    // Replace path parameters
    Object.keys(pathParams).forEach(key => {
      url = url.replace(`{${key}}`, pathParams[key] || `{${key}}`);
    });

    // Add query parameters
    const queryString = Object.entries(queryParams)
      .filter(([_, value]) => value !== '')
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join('&');

    if (queryString) {
      url += `?${queryString}`;
    }

    return url;
  };

  const handleExecute = async () => {
    setLoading(true);
    setError(null);
    setResponse(null);

    const url = buildUrl();
    const options = {
      method: method,
      headers: {
        'Content-Type': 'application/json',
      },
    };

    // Add API key to headers (not needed for POST /users/)
    if (apiKey && path !== '/users/') {
      options.headers['X-API-Key'] = apiKey;
    }

    // Add body for POST, PUT, PATCH
    if (['POST', 'PUT', 'PATCH'].includes(method) && Object.keys(bodyParams).length > 0) {
      options.body = JSON.stringify(bodyParams, null, 2);
    }

    try {
      const res = await fetch(url, options);
      const data = await res.text();

      let parsedData;
      try {
        parsedData = JSON.parse(data);
      } catch {
        parsedData = data;
      }

      setResponse({
        status: res.status,
        statusText: res.statusText,
        headers: Object.fromEntries(res.headers.entries()),
        data: parsedData,
        ok: res.ok,
      });
    } catch (err) {
      // Handle CORS and network errors
      let errorMessage = err.message;
      let errorType = 'Network Error';

      if (err.message.includes('Failed to fetch') || err.message.includes('CORS')) {
        errorMessage = 'CORS error: Make sure the API server is running and allows requests from this origin. The API may need CORS headers configured.';
        errorType = 'CORS Error';
      }

      setError({
        message: errorMessage,
        type: errorType,
      });
    } finally {
      setLoading(false);
    }
  };

  const copyResponse = () => {
    if (response) {
      navigator.clipboard.writeText(JSON.stringify(response.data, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getStatusColor = (status) => {
    if (status >= 200 && status < 300) return 'text-green-400';
    if (status >= 400 && status < 500) return 'text-yellow-400';
    if (status >= 500) return 'text-red-400';
    return 'text-gray-400';
  };

  return (
    <div className="mt-6 space-y-4">
      <div className="border border-gray-700 rounded-lg overflow-hidden">
          {/* API Key Input - Only show if endpoint requires auth */}
          {path !== '/users/' && (
            <div className="bg-gray-800 p-4 border-b border-gray-700">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                API Key
              </label>
              <input
                type="password"
                value={apiKey}
                onChange={(e) => handleApiKeyChange(e.target.value)}
                placeholder="email@example.com:your_api_key_here"
                className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Stored locally in your browser. Format: email:api_key
              </p>
            </div>
          )}

        {/* Path Parameters */}
        {Object.keys(pathParams).length > 0 && (
          <div className="bg-gray-800 p-4 border-b border-gray-700">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Path Parameters
            </label>
            <div className="space-y-2">
              {Object.keys(pathParams).map(key => (
                <div key={key}>
                  <label className="block text-xs text-gray-400 mb-1">
                    <code>{key}</code>
                  </label>
                  <input
                    type="text"
                    value={pathParams[key]}
                    onChange={(e) => setPathParams(prev => ({ ...prev, [key]: e.target.value }))}
                    placeholder={`Enter ${key}`}
                    className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Query Parameters */}
        {(parameters.query || parameters.queryParams) && (
          <div className="bg-gray-800 p-4 border-b border-gray-700">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Query Parameters
            </label>
            <div className="space-y-2">
              {Object.keys(parameters.query || parameters.queryParams || {}).map(key => {
                const param = (parameters.query || parameters.queryParams || {})[key];
                const defaultValue = param?.default !== undefined ? String(param.default) : '';
                return (
                  <div key={key}>
                    <label className="block text-xs text-gray-400 mb-1">
                      <code>{key}</code>
                      {param?.required && <span className="text-red-400 ml-1">*</span>}
                    </label>
                    <input
                      type="text"
                      value={queryParams[key] !== undefined ? queryParams[key] : defaultValue}
                      onChange={(e) => setQueryParams(prev => ({ ...prev, [key]: e.target.value }))}
                      placeholder={param?.description || `Enter ${key}`}
                      className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Request Body */}
        {requestBody && ['POST', 'PUT', 'PATCH'].includes(method) && (
          <div className="bg-gray-800 p-4 border-b border-gray-700">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Request Body
            </label>
            <textarea
              value={JSON.stringify(bodyParams, null, 2)}
              onChange={(e) => {
                try {
                  setBodyParams(JSON.parse(e.target.value));
                } catch {
                  // Invalid JSON, ignore
                }
              }}
              rows={8}
              className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded text-white font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Edit JSON directly. Invalid JSON will not be sent.
            </p>
          </div>
        )}

        {/* Execute Button */}
        <div className="bg-gray-800 p-4">
          <button
            onClick={handleExecute}
            disabled={loading || (path !== '/users/' && !apiKey)}
            className={`flex items-center gap-2 px-4 py-2 rounded font-medium transition-colors ${
              loading || (path !== '/users/' && !apiKey)
                ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Sending Request...
              </>
            ) : (
              <>
                <Play className="w-4 h-4" />
                Send Request
              </>
            )}
          </button>
          {path !== '/users/' && !apiKey && (
            <p className="text-xs text-yellow-400 mt-2">
              Please enter your API key to send requests
            </p>
          )}
          {path === '/users/' && (
            <p className="text-xs text-blue-400 mt-2">
              This endpoint doesn't require authentication
            </p>
          )}
        </div>
      </div>

      {/* Response */}
      {response && (
        <div className="border border-gray-700 rounded-lg overflow-hidden">
          <div className="bg-gray-800 p-4 border-b border-gray-700 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className={`font-semibold ${getStatusColor(response.status)}`}>
                {response.status} {response.statusText}
              </span>
            </div>
            <button
              onClick={copyResponse}
              className="flex items-center gap-1 px-2 py-1 text-xs text-gray-400 hover:text-white transition-colors"
            >
              {copied ? (
                <>
                  <Check className="w-3 h-3" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="w-3 h-3" />
                  Copy
                </>
              )}
            </button>
          </div>
          <div className="bg-gray-900 p-4">
            <pre className="text-sm text-gray-300 overflow-x-auto">
              <code>{JSON.stringify(response.data, null, 2)}</code>
            </pre>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="border border-red-700 rounded-lg overflow-hidden bg-red-900/20">
          <div className="bg-red-900/30 p-4 border-b border-red-700 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-400" />
            <span className="font-semibold text-red-400">{error.type}</span>
          </div>
          <div className="p-4 text-sm text-red-300">
            {error.message}
          </div>
        </div>
      )}

      {/* Request URL Display */}
      {!loading && (
        <div className="text-xs text-gray-500 p-2 bg-gray-800/50 rounded">
          <strong>Request URL:</strong> <code className="text-blue-400">{buildUrl()}</code>
        </div>
      )}
    </div>
  );
};

export default ApiExplorer;
